import { useTranslation } from "@coworker/locales";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FutureTask } from "./FutureTask/FutureTask";
import { RepeatingTask } from "./RepeatingTask/RepeatingTask";
import { getScheduleTypeTitle, ScheduleType } from "../helper";
import {
  MonthlyRecurrenceType,
  RecurrencePeriod,
  WeeklyRecurrenceDay,
} from "@coworker/apprestructured/src/task-management/types/Schedule";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import ModalColumn from "../../ModalColumn/ModalColumn";

type ScheduleModalProps = {
  showModal: boolean;
  scheduleType: ScheduleType;
  futureStart: string | null;
  isRecurrencePeriodValid: boolean;
  recurrencePeriod: RecurrencePeriod | null;
  repeatingStart: string | null;
  end: string | null;
  startDateError: string | null;
  endDateError: string | null;
  monthlyRecurrenceType: MonthlyRecurrenceType;
  weeklyRecurrenceDays: WeeklyRecurrenceDay[];
  onScheduleTypeChange: (scheduleType: ScheduleType) => void;
  onFutureStartChange: (futureStart: string | null) => void;
  onRecurrencePeriodChange: (recurrencePeriod: RecurrencePeriod) => void;
  onRepeatingStartChange: (repeatingStart: string | null) => void;
  onEndChange: (end: string | null) => void;
  onConfirmClick: () => void;
  onCloseModal: () => void;
  onMonthlyRecurrenceTypeChange: (
    monthlyRecurrenceType: MonthlyRecurrenceType
  ) => void;
  onWeeklyRecurrenceDaysChange: (
    weeklyRecurrenceDays: WeeklyRecurrenceDay[]
  ) => void;
};

function ScheduleModal(props: ScheduleModalProps) {
  const { t } = useTranslation();

  return (
    <FixaModal visible={props.showModal} handleCloseBtn={props.onCloseModal}>
      <FixaSheets
        header={
          <FixaModalHeader
            closeBtnClick={props.onCloseModal}
            title={getScheduleTypeTitle(props.scheduleType, t)}
          />
        }
        footer={
          <FixaModalFooter renderBorder>
            <FixaButton
              text={t("confirmString")}
              type="primary"
              onClick={props.onConfirmClick}
              fluid
            />
          </FixaModalFooter>
        }
        fullSize
      >
        <FixaModalBody>
          <ModalColumn gap="big">
            <FixaListView id="typeSelector" size="small">
              <FixaListViewItem
                title={t("dontScheduleOrRepeatTaskString")}
                control="radiobutton"
                controlProps={{
                  checked: props.scheduleType === ScheduleType.NO_REPEAT,
                  value: ScheduleType.NO_REPEAT,
                }}
                onChange={() =>
                  props.onScheduleTypeChange(ScheduleType.NO_REPEAT)
                }
              />
              <FixaListViewItem
                title={t("scheduleTaskForFutureString")}
                control="radiobutton"
                controlProps={{
                  checked: props.scheduleType === ScheduleType.FUTURE,
                  value: ScheduleType.FUTURE,
                }}
                onChange={() => props.onScheduleTypeChange(ScheduleType.FUTURE)}
              />
              <FixaListViewItem
                title={t("createRepeatingTaskString")}
                control="radiobutton"
                controlProps={{
                  checked: props.scheduleType === ScheduleType.REPEATING,
                  value: ScheduleType.REPEATING,
                }}
                onChange={() =>
                  props.onScheduleTypeChange(ScheduleType.REPEATING)
                }
              />
            </FixaListView>
            {props.scheduleType === ScheduleType.FUTURE && (
              <FutureTask
                start={props.futureStart}
                startDateError={props.startDateError}
                onStartChange={props.onFutureStartChange}
              />
            )}
            {props.scheduleType === ScheduleType.REPEATING && (
              <RepeatingTask
                start={props.repeatingStart}
                end={props.end}
                startDateError={props.startDateError}
                endDateError={props.endDateError}
                recurrencePeriod={props.recurrencePeriod}
                isRecurrencePeriodValid={props.isRecurrencePeriodValid}
                monthlyRecurrenceType={props.monthlyRecurrenceType}
                weeklyRecurrenceDays={props.weeklyRecurrenceDays}
                onRecurrencePeriodChange={props.onRecurrencePeriodChange}
                onStartChange={props.onRepeatingStartChange}
                onEndChange={props.onEndChange}
                onMonthlyRecurrenceTypeChange={
                  props.onMonthlyRecurrenceTypeChange
                }
                onWeeklyRecurrenceDaysChange={
                  props.onWeeklyRecurrenceDaysChange
                }
              />
            )}
          </ModalColumn>
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
}

export default ScheduleModal;
